export type CindroSession = {
  accessToken: string;
  refreshToken: string;
  user: {
    tenantUserId: string;
    organizationId: string;
    email: string;
    displayName: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
    isActive: boolean;
    isSuperAdmin: boolean;
    lastLoginAt?: string | null;
  };
};

export type SessionChangeListener = (session: CindroSession | null) => void;

const REFRESH_REUSE_WINDOW_MS = 10_000;
let refreshSessionPromise: Promise<CindroSession | null> | null = null;
let recentRefreshSession: {
  refreshToken: string;
  session: CindroSession;
  refreshedAt: number;
} | null = null;
let sessionChangeListener: SessionChangeListener | null = null;

export function getSessionToken() {
  return window.localStorage.getItem('cindro-token') || '';
}

export function getRefreshToken() {
  return window.localStorage.getItem('cindro-refresh-token') || '';
}

export function setSessionToken(token: string) {
  window.localStorage.setItem('cindro-token', token);
}

export function setRefreshToken(token: string) {
  window.localStorage.setItem('cindro-refresh-token', token);
}

export function setSessionTokens(session: CindroSession) {
  setSessionToken(session.accessToken);
  setRefreshToken(session.refreshToken);
}

export function clearSessionToken() {
  window.localStorage.removeItem('cindro-token');
  window.localStorage.removeItem('cindro-refresh-token');
  recentRefreshSession = null;
}

export function subscribeSessionChanges(listener: SessionChangeListener) {
  sessionChangeListener = listener;
  return () => {
    if (sessionChangeListener === listener) {
      sessionChangeListener = null;
    }
  };
}

export function emitSessionChange(session: CindroSession | null) {
  sessionChangeListener?.(session);
}

export async function recoverSessionFromRefreshToken(
  refreshRequest: (refreshToken: string) => Promise<CindroSession>
): Promise<CindroSession | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  if (
    recentRefreshSession?.refreshToken === refreshToken &&
    Date.now() - recentRefreshSession.refreshedAt < REFRESH_REUSE_WINDOW_MS
  ) {
    return recentRefreshSession.session;
  }

  if (!refreshSessionPromise) {
    refreshSessionPromise = refreshRequest(refreshToken)
      .then((session) => {
        setSessionTokens(session);
        recentRefreshSession = {
          refreshToken: session.refreshToken,
          session,
          refreshedAt: Date.now()
        };
        emitSessionChange(session);
        return session;
      })
      .catch((): null => null)
      .finally(() => {
        refreshSessionPromise = null;
      });
  }

  return refreshSessionPromise;
}
